import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from './ThemeContext';
import { RFP_SECTIONS, buildEmptyRfpState, mergeRfpFromSaved } from '../data/rfpFieldConfig';
import { PLACEMENT_CHANNEL_OPTIONS } from '../data/rfpOptionLists';

export function setRfpField(sectionId, key, value, prev) {
  return {
    ...prev,
    [sectionId]: { ...prev[sectionId], [key]: value },
  };
}

function channelLabel(value) {
  return PLACEMENT_CHANNEL_OPTIONS.find((o) => o.value === value)?.label || value;
}

function parseMoney(v) {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/,/g, '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : null;
}

export function ZipTagField({ sectionId, fieldKey, value, setRfp, inputCls, placeholder }) {
  const [draft, setDraft] = useState('');
  const [bulk, setBulk] = useState('');
  const list = Array.isArray(value) ? value : [];

  const addTokens = (tokens) => {
    const next = new Set(list);
    for (const t of tokens) {
      const z = t.trim();
      if (z) next.add(z);
    }
    setRfp((prev) => setRfpField(sectionId, fieldKey, [...next], prev));
  };

  const add = () => {
    const z = draft.trim();
    if (!z) return;
    addTokens([z]);
    setDraft('');
  };

  const addBulk = () => {
    const parts = bulk.split(/[,;\s\n\r\t]+/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    addTokens(parts);
    setBulk('');
  };

  const remove = (z) => {
    setRfp((prev) => setRfpField(sectionId, fieldKey, list.filter((x) => x !== z), prev));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {list.map((z) => (
          <span
            key={z}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs"
          >
            {z}
            <button type="button" className="text-red-600 hover:underline" onClick={() => remove(z)}>
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className={inputCls}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" onClick={add} className="px-3 py-2 rounded-lg bg-gray-200 dark:bg-gray-600 text-sm shrink-0">
          Add
        </button>
      </div>
      <div>
        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Paste multiple ZIPs (commas, spaces, or new lines)</label>
        <textarea
          className={`${inputCls} min-h-[64px] font-mono text-xs`}
          placeholder="60614, 10001 90210"
          value={bulk}
          onChange={(e) => setBulk(e.target.value)}
        />
        <button type="button" onClick={addBulk} className="mt-1 px-3 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-600 text-xs">
          Add all from paste
        </button>
      </div>
    </div>
  );
}

export function StructuredField({ f, secId, rfp, setRfp, inputCls, rfpAll }) {
  const v = rfp?.[f.key];
  const setV = (next) => setRfp((prev) => setRfpField(secId, f.key, next, prev));

  if (f.input === 'readonly') {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
        <p className="text-sm text-gray-800 dark:text-gray-200">{f.readonlyText}</p>
      </div>
    );
  }

  if (f.input === 'channelBudgets') {
    const channels = Array.isArray(rfpAll.inventory?.placementChannels) ? rfpAll.inventory.placementChannels : [];
    const alloc =
      rfpAll.budgetPricing?.budgetAllocations && typeof rfpAll.budgetPricing.budgetAllocations === 'object'
        ? rfpAll.budgetPricing.budgetAllocations
        : {};
    if (channels.length <= 1) {
      return (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {channels.length === 0
            ? 'Select channels in “Inventory & placement” first. If only one channel applies, use the buyer budget field above.'
            : 'Only one channel selected — use the buyer budget field above, or add another channel to allocate by line item.'}
        </p>
      );
    }
    return (
      <div className="space-y-3">
        {channels.map((ch) => (
          <div key={ch}>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {channelLabel(ch)} ($)
            </label>
            <input
              className={inputCls}
              type="text"
              inputMode="decimal"
              value={alloc[ch] != null ? String(alloc[ch]) : ''}
              onChange={(e) => {
                const next = { ...alloc, [ch]: e.target.value };
                setRfp((prev) => setRfpField('budgetPricing', 'budgetAllocations', next, prev));
              }}
              placeholder="0"
            />
          </div>
        ))}
      </div>
    );
  }

  if (f.input === 'radio' && f.options) {
    return (
      <div className="flex flex-wrap gap-2">
        {f.options.map((opt) => (
          <label
            key={opt.value}
            className={`inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border text-sm transition-colors ${
              v === opt.value
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-white'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-800 dark:text-gray-200'
            }`}
          >
            <input
              type="radio"
              className="text-blue-600"
              name={`rfp-${secId}-${f.key}`}
              checked={v === opt.value}
              onChange={() => setV(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </div>
    );
  }

  if (f.input === 'multicheck' && f.options) {
    const arr = Array.isArray(v) ? v : [];
    const toggle = (value) => {
      const s = new Set(arr);
      if (s.has(value)) s.delete(value);
      else s.add(value);
      setV([...s]);
    };
    return (
      <div className="grid sm:grid-cols-2 gap-2">
        {f.options.map((opt) => (
          <label
            key={opt.value}
            className="flex items-start gap-2 cursor-pointer text-sm text-gray-800 dark:text-gray-200 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-gray-600 px-2 py-1"
          >
            <input type="checkbox" className="mt-0.5 rounded text-blue-600" checked={arr.includes(opt.value)} onChange={() => toggle(opt.value)} />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (f.input === 'multiselect' && f.options) {
    const arr = Array.isArray(v) ? v : [];
    return (
      <select
        multiple
        size={f.selectSize || 6}
        className={`${inputCls} min-h-[8rem]`}
        value={arr}
        onChange={(e) => {
          const selected = [...e.target.selectedOptions].map((o) => o.value);
          setV(selected);
        }}
      >
        {f.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  if (f.input === 'tags') {
    return (
      <ZipTagField
        sectionId={secId}
        fieldKey={f.key}
        value={v}
        setRfp={setRfp}
        inputCls={inputCls}
        placeholder={f.placeholder || 'ZIP'}
      />
    );
  }

  if (f.input === 'date') {
    return (
      <input type="date" className={inputCls} value={v || ''} onChange={(e) => setV(e.target.value)} />
    );
  }

  if (f.input === 'select' && f.options) {
    return (
      <select className={inputCls} value={v || ''} onChange={(e) => setV(e.target.value)}>
        <option value="">— Select —</option>
        {f.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  if (f.input === 'textarea') {
    return (
      <textarea
        className={`${inputCls} min-h-[72px]`}
        rows={3}
        value={v || ''}
        onChange={(e) => setV(e.target.value)}
      />
    );
  }

  return (
    <input className={inputCls} value={v || ''} onChange={(e) => setV(e.target.value)} />
  );
}

function validateChannelBudgets(rfp, forSend) {
  const channels = Array.isArray(rfp.inventory?.placementChannels) ? rfp.inventory.placementChannels : [];
  if (!forSend || channels.length <= 1) return '';
  const alloc = rfp.budgetPricing?.budgetAllocations || {};
  for (const c of channels) {
    const n = parseMoney(alloc[c]);
    if (n == null || n <= 0) {
      return `Enter a valid budget ($) for each selected channel, including “${channelLabel(c)}”.`;
    }
  }
  return '';
}

function validateDmaOrCbsa(rfp) {
  const dma = rfp.audience?.dmaMarkets;
  const cbsa = rfp.audience?.cbsaMarkets;
  const d = Array.isArray(dma) ? dma.length : 0;
  const c = Array.isArray(cbsa) ? cbsa.length : 0;
  if (d + c < 1) return 'Audience targeting: select at least one DMA or one CBSA.';
  return '';
}

function validateProposalSend(rfp, forSend) {
  if (!forSend) return '';
  const cb = rfp.proposalResponse?.competitiveBids;
  if (cb !== 'yes' && cb !== 'no') return 'Proposal response: choose Yes or No for “Competitive bids solicited?”.';
  return '';
}

const RfpForm = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [campaignTitle, setCampaignTitle] = useState('');
  const [quotedPrice, setQuotedPrice] = useState('');
  const [rfpBudget, setRfpBudget] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [ccSalesperson, setCcSalesperson] = useState(true);
  const [rfp, setRfp] = useState(() => buildEmptyRfpState());
  const [savedId, setSavedId] = useState(null);
  const [openSection, setOpenSection] = useState(RFP_SECTIONS[0]?.id || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [myRfps, setMyRfps] = useState([]);

  const loadOrder = useCallback(async (id) => {
    const r = await fetch(`/api/order/${id}`, { credentials: 'include' });
    if (!r.ok) throw new Error('Could not load RFP');
    const o = await r.json();
    if (o.record_kind !== 'rfp') throw new Error('Not an RFP record');
    setSavedId(o.id);
    setCampaignTitle(o.campaign_name || '');
    setQuotedPrice(o.quoted_price != null ? String(o.quoted_price) : '');
    setRfpBudget(o.budget != null ? String(o.budget) : '');
    setRecipientName(o.recipient_name || '');
    setRecipientEmail(o.recipient_email || '');
    const blob = o.form_data_json && typeof o.form_data_json === 'object' ? o.form_data_json : {};
    if (blob.rfp && typeof blob.rfp === 'object') {
      const merged = mergeRfpFromSaved(blob.rfp, buildEmptyRfpState());
      if (o.start_date && !merged.flighting.flightScheduleStart) {
        merged.flighting.flightScheduleStart = o.start_date.slice(0, 10);
      }
      if (o.end_date && !merged.flighting.flightScheduleEnd) {
        merged.flighting.flightScheduleEnd = o.end_date.slice(0, 10);
      }
      setRfp(merged);
    } else {
      const empty = buildEmptyRfpState();
      if (o.start_date) empty.flighting.flightScheduleStart = o.start_date.slice(0, 10);
      if (o.end_date) empty.flighting.flightScheduleEnd = o.end_date.slice(0, 10);
      setRfp(empty);
    }
  }, []);

  useEffect(() => {
    if (editId) {
      loadOrder(Number(editId)).catch((e) => setMsg(e.message));
    }
  }, [editId, loadOrder]);

  useEffect(() => {
    const loadList = async () => {
      try {
        const r = await fetch('/api/orders?recordKind=rfp', { credentials: 'include' });
        if (r.ok) setMyRfps(await r.json());
      } catch {
        /* ignore */
      }
    };
    loadList();
  }, [savedId]);

  const validateCore = (forSend) => {
    if (!(campaignTitle || '').trim()) return 'Campaign / deal name is required.';
    if (!(rfp.identity?.advertiserName || '').trim()) return 'Advertiser name is required (Identity section).';
    if (forSend && !(recipientEmail || '').trim()) return 'Recipient email is required to send the proposal.';
    const geoErr = validateDmaOrCbsa(rfp);
    if (geoErr) return geoErr;
    const bErr = validateChannelBudgets(rfp, forSend);
    if (bErr) return bErr;
    const pErr = validateProposalSend(rfp, forSend);
    if (pErr) return pErr;
    return '';
  };

  const submitPayload = async (rfpAction) => {
    const err = validateCore(rfpAction === 'send');
    if (err) {
      setMsg(err);
      return;
    }
    setBusy(true);
    setMsg('');
    const rfpOut = JSON.parse(JSON.stringify(rfp));
    rfpOut.budgetPricing = { ...rfpOut.budgetPricing, paymentTerms: 'NET 30' };
    const flightStart = rfpOut.flighting?.flightScheduleStart || null;
    const flightEnd = rfpOut.flighting?.flightScheduleEnd || null;
    try {
      const body = {
        recipientEmail: (recipientEmail || '').trim(),
        recipientName: (recipientName || '').trim(),
        quotedPrice,
        rfpBudget,
        campaignTitle: (campaignTitle || '').trim(),
        advertiserName: (rfp.identity?.advertiserName || '').trim(),
        contactEmail: user?.mail || user?.email || '',
        startDate: flightStart,
        endDate: flightEnd,
        formDataJson: JSON.stringify({ rfpVersion: 3 }),
        rfpJson: JSON.stringify(rfpOut),
        ccSalesperson: ccSalesperson ? '1' : '0',
        rfpAction,
      };

      if (savedId) {
        const r = await fetch(`/api/orders/${savedId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || j.details || 'Update failed');
        setMsg(rfpAction === 'send' ? 'Proposal sent.' : 'Draft saved.');
        if (j.id) setSavedId(j.id);
        return;
      }

      const fd = new FormData();
      fd.append('recordKind', 'rfp');
      fd.append('rfpAction', rfpAction);
      fd.append('campaignTitle', body.campaignTitle);
      fd.append('advertiserName', body.advertiserName);
      fd.append('contactEmail', body.contactEmail);
      fd.append('recipientEmail', body.recipientEmail);
      fd.append('recipientName', body.recipientName);
      fd.append('quotedPrice', body.quotedPrice || '');
      fd.append('rfpBudget', body.rfpBudget || '');
      if (body.startDate) fd.append('startDate', body.startDate);
      if (body.endDate) fd.append('endDate', body.endDate);
      fd.append('formDataJson', body.formDataJson);
      fd.append('rfpJson', body.rfpJson);
      fd.append('ccSalesperson', body.ccSalesperson);

      const r = await fetch('/api/orders', { method: 'POST', body: fd, credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || j.details || 'Save failed');
      setSavedId(j.id);
      setMsg(rfpAction === 'send' ? 'Proposal sent.' : `Draft saved (reference #${j.id}).`);
    } catch (e) {
      setMsg(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm';

  if (user?.userType === 'venue' || user?.userType === 'affiliate') {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center">
        <p className="text-gray-700 dark:text-gray-300">RFP intake is for internal and contracted sales users.</p>
        <Link className="text-blue-600 mt-4 inline-block" to="/app">
          Back to order form
        </Link>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen py-8 px-4 ${isDark ? 'bg-gray-900' : 'bg-slate-50'}`}
    >
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">CPMN® RFP intake</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Structured selections reduce errors. Use notes fields only where needed. No help-desk ticket is created
              until an IO is submitted after the buyer accepts.
            </p>
          </div>
          <Link
            to="/admin"
            className="text-sm text-blue-600 dark:text-blue-400 font-medium"
          >
            Admin dashboard →
          </Link>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4 shadow-sm">
          <h2 className="font-semibold text-gray-900 dark:text-white">Deal & recipient</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Campaign / deal name *
              </label>
              <input className={inputCls} value={campaignTitle} onChange={(e) => setCampaignTitle(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Flight start</label>
              <input
                type="date"
                className={inputCls}
                value={rfp.flighting?.flightScheduleStart || ''}
                onChange={(e) =>
                  setRfp((p) => ({
                    ...p,
                    flighting: { ...p.flighting, flightScheduleStart: e.target.value },
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Flight end</label>
              <input
                type="date"
                className={inputCls}
                value={rfp.flighting?.flightScheduleEnd || ''}
                onChange={(e) =>
                  setRfp((p) => ({
                    ...p,
                    flighting: { ...p.flighting, flightScheduleEnd: e.target.value },
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Your quoted investment ($)
              </label>
              <input className={inputCls} value={quotedPrice} onChange={(e) => setQuotedPrice(e.target.value)} placeholder="e.g. 45000" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Buyer-stated budget ($), if any
              </label>
              <input className={inputCls} value={rfpBudget} onChange={(e) => setRfpBudget(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Recipient name</label>
              <input className={inputCls} value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Recipient email</label>
              <input className={inputCls} type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input
                id="ccself"
                type="checkbox"
                checked={ccSalesperson}
                onChange={(e) => setCcSalesperson(e.target.checked)}
              />
              <label htmlFor="ccself" className="text-sm text-gray-700 dark:text-gray-300">
                CC me on the proposal email (keeps a copy in your mailbox; Reply-To is set to you)
              </label>
            </div>
          </div>
        </div>

        {RFP_SECTIONS.map((sec) => (
          <div
            key={sec.id}
            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-sm"
          >
            <button
              type="button"
              className="w-full flex justify-between items-center px-4 py-3 text-left font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50"
              onClick={() => setOpenSection((s) => (s === sec.id ? '' : sec.id))}
            >
              {sec.title}
              <span className="text-gray-400 text-sm">{openSection === sec.id ? '−' : '+'}</span>
            </button>
            {openSection === sec.id && (
              <div className="p-4 space-y-4 border-t border-gray-100 dark:border-gray-700">
                {sec.fields.map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {f.label}
                      {f.required || f.key === 'advertiserName' ? ' *' : ''}
                    </label>
                    {f.help ? <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{f.help}</p> : null}
                    {f.input === 'multiselect' ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Hold Ctrl (Windows) or ⌘ (Mac) to select multiple.</p>
                    ) : null}
                    <StructuredField f={f} secId={sec.id} rfp={rfp[sec.id] || {}} setRfp={setRfp} inputCls={inputCls} rfpAll={rfp} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {msg && (
          <div
            className={`rounded-lg px-4 py-3 text-sm ${
              msg.includes('sent') || msg.includes('saved')
                ? 'bg-green-50 text-green-900 dark:bg-green-900/30 dark:text-green-200'
                : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'
            }`}
          >
            {msg}
            {savedId ? (
              <span className="block mt-1 font-mono text-xs">
                Reference #{savedId}
                <a
                  className="ml-2 text-blue-600 underline"
                  href={`/api/orders/${savedId}/rfp-pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download PDF
                </a>
              </span>
            ) : null}
          </div>
        )}

        <div className="flex flex-wrap gap-3 pb-12">
          <button
            type="button"
            disabled={busy}
            onClick={() => submitPayload('draft')}
            className="px-5 py-2.5 rounded-lg bg-gray-800 text-white text-sm font-medium disabled:opacity-50"
          >
            Save draft
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => submitPayload('send')}
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
          >
            Save &amp; email PDF proposal
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-3">Your RFPs</h2>
          {myRfps.length === 0 ? (
            <p className="text-sm text-gray-500">No RFPs yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 text-sm">
              {myRfps.map((o) => (
                <li key={o.id} className="py-2 flex justify-between gap-2">
                  <span className="text-gray-800 dark:text-gray-200">
                    #{o.id} — {o.campaign_name || 'Untitled'}{' '}
                    <span className="text-gray-500">({o.status})</span>
                  </span>
                  <div className="flex gap-2 shrink-0">
                    <Link className="text-blue-600" to={`/rfp?edit=${o.id}`}>
                      Edit
                    </Link>
                    <a className="text-blue-600" href={`/api/orders/${o.id}/rfp-pdf`} target="_blank" rel="noreferrer">
                      PDF
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default RfpForm;
